/**
 * Bridges puzzle generator wrapper.
 *
 * Wraps `bridges-generator`'s synchronous `generate(...)` with three concerns:
 *
 * 1. **Output cache** keyed by the full `bridges:rows:cols:islands:double` parameter
 *    string. The first generation for a given parameter string is persisted to
 *    `localStorage` under the `bridgesGeneratorCache:v1:` prefix. Subsequent loads
 *    (page reload, navigate-away-and-back) return the *exact same* puzzle JSON,
 *    so `parseBridgesPuzzleConfig` produces the same deterministic `puzzleID` and
 *    `BridgesGameState.getByID` resumes any bridges the player had placed.
 *
 *    Storage choice: `localStorage` (not `sessionStorage`) to match the NGXS
 *    storage plugin's `bridgesGameStates` lifetime — the two must outlive a
 *    page reload together.
 *
 * 2. **Predictive generation-time hint** for the call site. The puzzle is large
 *    enough on a mid-range device that some boards take seconds to generate on
 *    the main thread. Rather than freezing the UI silently, the caller can ask
 *    `shouldPromptBeforeGenerate(...)` whether to show a user-facing prompt and
 *    offer a "Pick smaller" escape hatch via `getSmallerBridgesParams(...)`. The
 *    cache short-circuits this on the second-and-later visit per device.
 *
 * 3. **Surfaced failures**. `bridges-generator` occasionally throws on certain
 *    parameter combinations (a known internal bug in `bridges-solver`'s
 *    `connectTo`). We retry up to `MAX_GENERATE_RETRIES` times to find a Math.random
 *    state that works (this is what was previously hidden behind a 3x3 silent
 *    fallback) and only then surface the failure via `console.error` + a typed
 *    `BridgesGeneratorError`. The application's global ErrorHandler
 *    (`Sentry.createErrorHandler` in `web`/`app.module.ts`) picks it up at the
 *    boundary; this library MUST NOT import `@sentry/angular` directly.
 *
 * ---------------------------------------------------------------------------
 * DO NOT RELY ON THIS PATH FOR SHIPPABLE PUZZLES.
 *
 * No project config uses `generated:bridges:...` any more. `public_demo` serves
 * pre-baked JSON assets produced by `scripts/bake-bridges-puzzles.mjs`, because
 * generation cannot be made safe in the browser: `generate()` is synchronous on
 * the main thread with no way to abort it, so a bad seed is a frozen tab rather
 * than a slow one. Re-enabling `generated:bridges:...` for a customer-facing
 * build needs the generator moved into a terminable worker first.
 *
 * `PREDICTED_DURATION_TABLE` and `SLOW_THRESHOLD_MS` below are KNOWN WRONG and
 * are kept only so the existing call path keeps compiling. They were tuned from
 * medians over 5 runs of `scripts/measure-bridges-generator.mjs` (which still
 * exists, despite what this comment used to claim), and a median hides the
 * behaviour that actually matters. Re-measured with a hard per-attempt timeout:
 *
 *   - cols > rows throws on all `MAX_GENERATE_RETRIES` attempts, every time.
 *     5:7:13 failed 30/30; transposing to 7:5:13 succeeded on the first try.
 *     6:8:18 burned 281 SECONDS before throwing.
 *   - 7:7:19 exceeded 8s on 3 of 3 runs and one successful first-attempt run
 *     took 50.9s, against a table entry of ~30ms — three orders of magnitude
 *     out, and under `SLOW_THRESHOLD_MS`, so the prompt never fired for it.
 *   - the same config is not self-consistent: 11:11:22 measured 42ms, 67ms,
 *     then a timeout. No allowlist of parameters is safe at runtime.
 *
 * `BRIDGES_TIER_LIST` is ordered by board area, but suitability tracks island
 * density and grid shape, so `getSmallerBridgesParams` can hand back something
 * slower than what the player started with.
 * ---------------------------------------------------------------------------
 */

import { PuzzleTypeIdEnum } from '../../puzzle-framework-module/puzzle-type-id.enum';
import { generate } from 'bridges-generator';

export const BRIDGES_CACHE_PREFIX = 'bridgesGeneratorCache:v1:';

// Threshold above which the call site should ask the user before generating.
// Tuned so 12x16x61 and above trip the prompt; 10x10x25 and below do not.
export const SLOW_THRESHOLD_MS = 800;

// Multiplier applied to lab measurements to approximate a mid-range device.
// Lab numbers were captured on an M-series Mac; phones are ~3-4x slower.
export const DEVICE_PROFILE_MULT = 3.5;

// `bridges-generator` throws on some Math.random sequences. Empirically, even
// "stable" sizes throw <5% of the time and almost always pass on retry. Some
// asymmetric configs (e.g. 5x7x13) fail far more often. 30 retries is generous
// without being pathological.
const MAX_GENERATE_RETRIES = 30;

export interface BridgesGenParams {
  rows: number;
  cols: number;
  islands: number;
  doubleBridges: number;
}

export interface BridgesGeneratedPuzzle {
  type: PuzzleTypeIdEnum;
  id: string;
  puzzle: number[][];
  solution: string[][];
}

export class BridgesGeneratorError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BridgesGeneratorError';
  }
}

interface PredictionEntry {
  rowsMax: number;
  colsMax: number;
  islandsMax: number;
  // Wall-clock ms on the reference device including expected retries.
  predictedMs: number;
}

// Lab measurements (M-series, Node 24, median ms over 5 runs incl. retries):
//   5x5x10    ~30      6x6x12     ~2     7x7x19     ~30
//   5x7x13    BROKEN   6x8x18     BROKEN 7x9x23     ~100
//   7x10x25   ~200     7x10x28    ~400   8x10x33    ~600
//   8x11x31   ~300     9x13x38    ~500   10x10x25   ~80
//   10x14x49  ~800     12x16x61   ~1500  14x18x90   ~3500
//   15x15x50  ~2500
//
// BROKEN entries reliably throw in `bridges-solver` regardless of retry count
// (a library bug in `connectTo` — `reverseConnection` undefined). They are
// listed for completeness; entries beyond ~6 retries effectively cap their
// success rate. Predictions for non-BROKEN configs are wall-clock and include
// the expected retry tail.
//
// Mid-device estimate = lab * DEVICE_PROFILE_MULT.
export const PREDICTED_DURATION_TABLE: PredictionEntry[] = [
  { rowsMax: 5, colsMax: 5, islandsMax: 10, predictedMs: 50 * DEVICE_PROFILE_MULT },
  { rowsMax: 6, colsMax: 6, islandsMax: 12, predictedMs: 30 * DEVICE_PROFILE_MULT },
  { rowsMax: 7, colsMax: 7, islandsMax: 19, predictedMs: 50 * DEVICE_PROFILE_MULT },
  { rowsMax: 7, colsMax: 10, islandsMax: 25, predictedMs: 200 * DEVICE_PROFILE_MULT },
  { rowsMax: 8, colsMax: 10, islandsMax: 33, predictedMs: 600 * DEVICE_PROFILE_MULT },
  { rowsMax: 8, colsMax: 11, islandsMax: 31, predictedMs: 300 * DEVICE_PROFILE_MULT },
  { rowsMax: 9, colsMax: 13, islandsMax: 38, predictedMs: 500 * DEVICE_PROFILE_MULT },
  { rowsMax: 10, colsMax: 10, islandsMax: 25, predictedMs: 80 * DEVICE_PROFILE_MULT },
  { rowsMax: 10, colsMax: 14, islandsMax: 49, predictedMs: 800 * DEVICE_PROFILE_MULT },
  { rowsMax: 12, colsMax: 16, islandsMax: 61, predictedMs: 1500 * DEVICE_PROFILE_MULT },
  { rowsMax: 14, colsMax: 18, islandsMax: 90, predictedMs: 3500 * DEVICE_PROFILE_MULT },
  { rowsMax: 15, colsMax: 15, islandsMax: 50, predictedMs: 2500 * DEVICE_PROFILE_MULT },
];

// Ordered smallest to largest. Used by `getSmallerBridgesParams`. Mirrors active
// entries in `client/projects/web/src/assets/projects/public_demo/config.yaml`.
export const BRIDGES_TIER_LIST: BridgesGenParams[] = [
  { rows: 5, cols: 5, islands: 10, doubleBridges: 0.2 },
  { rows: 5, cols: 7, islands: 13, doubleBridges: 0.2 },
  { rows: 6, cols: 6, islands: 12, doubleBridges: 0.2 },
  { rows: 6, cols: 8, islands: 18, doubleBridges: 0.2 },
  { rows: 7, cols: 7, islands: 15, doubleBridges: 0.2 },
  { rows: 7, cols: 7, islands: 19, doubleBridges: 0.2 },
  { rows: 7, cols: 9, islands: 23, doubleBridges: 0.2 },
  { rows: 7, cols: 10, islands: 25, doubleBridges: 0.2 },
  { rows: 7, cols: 10, islands: 28, doubleBridges: 0.2 },
  { rows: 8, cols: 10, islands: 33, doubleBridges: 0.2 },
  { rows: 8, cols: 11, islands: 31, doubleBridges: 0.2 },
  { rows: 9, cols: 13, islands: 38, doubleBridges: 0.2 },
  { rows: 10, cols: 10, islands: 25, doubleBridges: 0.2 },
  { rows: 10, cols: 14, islands: 49, doubleBridges: 0.2 },
  { rows: 12, cols: 16, islands: 61, doubleBridges: 0.2 },
  { rows: 14, cols: 18, islands: 90, doubleBridges: 0.2 },
  { rows: 15, cols: 15, islands: 50, doubleBridges: 0.2 },
];

export function parseBridgesGenParams(parameterString: string): BridgesGenParams {
  const parts = parameterString.split(':');
  if (parts[0] !== 'bridges') {
    throw new BridgesGeneratorError(`Invalid bridges parameter string (expected leading "bridges:"): "${parameterString}"`);
  }
  const rows = parsePositiveInt(parts[1], 10);
  const cols = parsePositiveInt(parts[2], 10);
  const islands = parsePositiveInt(parts[3], 5);
  const doubleBridges = parseBoundedFloat(parts[4], 0.2);
  return { rows, cols, islands, doubleBridges };
}

export function paramsToString(p: BridgesGenParams): string {
  return `bridges:${p.rows}:${p.cols}:${p.islands}:${p.doubleBridges}`;
}

function parsePositiveInt(s: string | undefined, fallback: number): number {
  if (s === undefined) return fallback;
  const v = parseInt(s, 10);
  if (Number.isNaN(v) || v <= 0) return fallback;
  return v;
}

function parseBoundedFloat(s: string | undefined, fallback: number): number {
  if (s === undefined) return fallback;
  const v = parseFloat(s);
  if (Number.isNaN(v) || v < 0 || v > 1) return fallback;
  return v;
}

export function predictGenerationMs(rows: number, cols: number, islands: number): number {
  for (const entry of PREDICTED_DURATION_TABLE) {
    if (rows <= entry.rowsMax && cols <= entry.colsMax && islands <= entry.islandsMax) {
      return entry.predictedMs;
    }
  }
  return PREDICTED_DURATION_TABLE[PREDICTED_DURATION_TABLE.length - 1].predictedMs;
}

export function shouldPromptBeforeGenerate(params: BridgesGenParams): boolean {
  return predictGenerationMs(params.rows, params.cols, params.islands) > SLOW_THRESHOLD_MS;
}

export function getSmallerBridgesParams(rows: number, cols: number, islands: number): BridgesGenParams {
  const tiers = BRIDGES_TIER_LIST;
  let currentIdx = -1;
  for (let i = 0; i < tiers.length; i++) {
    if (tiers[i].rows === rows && tiers[i].cols === cols && tiers[i].islands === islands) {
      currentIdx = i;
      break;
    }
  }
  if (currentIdx <= 0) {
    if (currentIdx === -1) {
      // Not on the tier list — pick the largest tier strictly smaller by area.
      const area = rows * cols;
      for (let i = tiers.length - 1; i >= 0; i--) {
        if (tiers[i].rows * tiers[i].cols < area) {
          return tiers[i];
        }
      }
    }
    return tiers[0];
  }
  return tiers[currentIdx - 1];
}

function cacheKey(parameterString: string): string {
  return `${BRIDGES_CACHE_PREFIX}${parameterString}`;
}

export function getCachedGeneratedBridges(parameterString: string): BridgesGeneratedPuzzle | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(cacheKey(parameterString));
    if (!raw) return null;
    return JSON.parse(raw) as BridgesGeneratedPuzzle;
  } catch {
    return null;
  }
}

function writeCache(parameterString: string, value: BridgesGeneratedPuzzle): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(cacheKey(parameterString), JSON.stringify(value));
  } catch {
    // Storage unavailable or quota exceeded — non-fatal.
  }
}

export function clearBridgesGeneratorCacheFor(parameterString: string): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(cacheKey(parameterString));
  } catch {
    // ignore
  }
}

export function clearAllBridgesGeneratorCache(): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(BRIDGES_CACHE_PREFIX)) toRemove.push(k);
    }
    for (const k of toRemove) localStorage.removeItem(k);
  } catch {
    // ignore
  }
}

function deterministicId(parameterString: string): string {
  return `bridges-${parameterString.replace(/:/g, '-')}`;
}

export function generateBridgesPuzzle(parameterString: string): BridgesGeneratedPuzzle {
  const cached = getCachedGeneratedBridges(parameterString);
  if (cached) return cached;

  const params = parseBridgesGenParams(parameterString);
  let produced: ReturnType<typeof generate> | undefined;
  let lastError: unknown = null;
  for (let attempt = 0; attempt < MAX_GENERATE_RETRIES; attempt++) {
    try {
      produced = generate(params.rows, params.cols, params.islands, params.doubleBridges);
      break;
    } catch (e) {
      lastError = e;
    }
  }
  if (!produced) {
    const msg = `Bridges generator failed after ${MAX_GENERATE_RETRIES} retries for "${parameterString}": ${
      (lastError as Error)?.message ?? lastError
    }`;
    console.error(msg);
    throw new BridgesGeneratorError(msg);
  }

  const result: BridgesGeneratedPuzzle = {
    type: PuzzleTypeIdEnum.BRIDGES,
    id: deterministicId(parameterString),
    puzzle: produced.puzzle,
    solution: produced.solution,
  };
  writeCache(parameterString, result);
  return result;
}
